import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Droplet, Activity, Scale, GlassWater, Moon, Dumbbell, Pill, Sparkles, User, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const navItems = [
  { to: "/", label: "Home", icon: LayoutDashboard, key: "nav-home" },
  { to: "/sugar", label: "Sugar", icon: Droplet, key: "nav-sugar" },
  { to: "/bp", label: "BP", icon: Activity, key: "nav-bp" },
  { to: "/weight", label: "Weight", icon: Scale, key: "nav-weight" },
  { to: "/water", label: "Water", icon: GlassWater, key: "nav-water" },
  { to: "/sleep", label: "Sleep", icon: Moon, key: "nav-sleep" },
  { to: "/exercise", label: "Exercise", icon: Dumbbell, key: "nav-exercise" },
  { to: "/medication", label: "Meds", icon: Pill, key: "nav-medication" },
  { to: "/assistant", label: "AI", icon: Sparkles, key: "nav-ai" },
];

export default function Layout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(false);
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white px-5 py-7" data-testid="sidebar">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-blue-600 text-white grid place-items-center font-bold">H</div>
          <div>
            <div className="font-heading font-bold text-slate-900 leading-tight">Health Guardian</div>
            <div className="text-xs text-slate-500">Your daily care companion</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, label, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={key}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm ${
                  isActive
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-slate-200">
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm ${
                isActive ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <User className="w-4 h-4" />
            <div className="flex-1 truncate">
              <div className="font-medium text-slate-900 truncate">{user?.name || "Profile"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-500 hover:bg-rose-50 hover:text-rose-600 text-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white grid place-items-center font-bold text-sm">H</div>
            <div className="font-heading font-bold text-slate-900">Health Guardian</div>
          </div>
          <NavLink to="/profile" data-testid="mobile-profile-btn" className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center">
            <User className="w-4 h-4 text-slate-600" />
          </NavLink>
        </header>
        <div className="pb-nav">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-2 pt-2 pb-safe" data-testid="bottom-nav">
        <div className="flex justify-between max-w-md mx-auto overflow-x-auto no-scrollbar">
          {navItems.slice(0, 5).map(({ to, label, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`mobile-${key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-xl min-w-[56px] ${
                  isActive ? "text-blue-700" : "text-slate-500"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={2.2} />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
          <NavLink
            to="/more"
            data-testid="mobile-nav-more"
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-xl min-w-[56px] ${
                isActive ? "text-blue-700" : "text-slate-500"
              }`
            }
          >
            <Sparkles className="w-5 h-5" strokeWidth={2.2} />
            <span className="text-[10px] font-medium">More</span>
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
